import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import BomMarkdownTextarea from './BomMarkdownTextarea';

function isExternalMarkdownLink(href) {
    const rawHref = (href || '').toString().trim();
    if (!rawHref || rawHref.startsWith('#')) return false;
    if (!/^(https?:)?\/\//i.test(rawHref)) return false;

    try {
      const browserOrigin = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';
      const parsed = new URL(rawHref, browserOrigin || 'https://stepwisedocs.local');
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
      if (!browserOrigin) return true;
      return parsed.origin !== browserOrigin;
    } catch (e) {
      return false;
    }
}

function MarkdownLink({ href, children, ...props }) {
    const isExternal = isExternalMarkdownLink(href);
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
}

function EditableTextArea ({
  name,
  value,
  index,
  onValueChange,
  path,
  contentStyle,
  editStyle,
  editable,
  showMarkdownHelp = true,
  markdownComponents,
  markdownRemarkPlugins,
  bomAutocompleteCatalog,
  guideAutocompleteCatalog,
  onCreateBomItem
}) {

    const [edit, setEdit] = useState(false);
    const [text, setText] = useState(value);
    const [showHelp, setShowHelp] = useState(false);
    const ignoreBlurRef = useRef(false);
    const textareaRef = useRef(null);

    useEffect(() => {
      if (edit) {
        document.getElementById(`${name}-${index}`)?.focus();
      }
    }, [edit, index, name]);

    useEffect(() => {
      setText(value);
    }, [value]);

    useEffect(() => {
      if (edit && !showHelp && textareaRef.current) {
        textareaRef.current.focus();
      }
    }, [edit, showHelp]);

    useEffect(() => {
      if (!showHelp) {
        ignoreBlurRef.current = false;
      }
    }, [showHelp]);

    const handleContentClick = (entryIndex, content) => {
      if (!editable) return;
      setEdit(true);
      setText(content);
    };

    const handleEditableBlur = (event) => {
      if (ignoreBlurRef.current || showHelp) {
        ignoreBlurRef.current = false;
        return;
      }
      const nextValue = event?.target?.value ?? text;
      setEdit(false);
      setText(nextValue);
      if (onValueChange) onValueChange(path, nextValue);
    };

    const textareaStyle = { ...editStyle };
    if (showMarkdownHelp && !textareaStyle.paddingRight) {
      textareaStyle.paddingRight = 36;
    }

    const mergedMarkdownComponents = {
      a: MarkdownLink,
      ...(markdownComponents || {})
    };

    return (
        ((edit && editable) ? (
          <>
            <BomMarkdownTextarea
              id={`${name}-${index}`}
              textareaRef={textareaRef}
              value={text}
              onValueChange={setText}
              onBlur={handleEditableBlur}
              style={{...textareaStyle}}
              wrapperClassName="editable-textarea-wrapper"
              bomAutocompleteCatalog={bomAutocompleteCatalog}
              guideAutocompleteCatalog={guideAutocompleteCatalog}
              onCreateBomItem={onCreateBomItem}
            >
              {showMarkdownHelp ? (
                <button
                  type="button"
                  className="markdown-help-trigger"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    ignoreBlurRef.current = true;
                  }}
                  onClick={() => setShowHelp(true)}
                  aria-label="Markdown help"
                >
                  <FontAwesomeIcon icon={faCircleQuestion} />
                </button>
              ) : null}
            </BomMarkdownTextarea>
            <Modal
              show={showHelp}
              onHide={() => setShowHelp(false)}
              centered
              animation={false}
            >
              <Modal.Header closeButton>
                <Modal.Title>Markdown help</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  Here you can use markdown. See the documentation:{' '}
                  <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noreferrer">
                    Markdown Guide
                  </a>
                </p>
                <div>
                  <strong>Colored bullets shortcuts:</strong>
                  <ul className="mt-2">
                    <li><code>- red Your text here</code></li>
                    <li><code>- yellow Your text here</code></li>
                    <li><code>- blue Your text here</code></li>
                    <li><code>- black Your text here</code></li>
                    <li><code>- green Your text here</code></li>
                    <li><code>- pink Your text here</code></li>
                    <li><code>- orange Your text here</code></li>
                    <li><code>- lightblue Your text here</code></li>
                    <li><code>- violet Your text here</code></li>
                    <li><code>- alert Your text here</code></li>
                    <li><code>- info Your text here</code></li>
                    <li><code>- hint Your text here</code></li>
                    <li><code>- #RRGGBB Your text here</code></li>
                  </ul>
                </div>
                <div>
                  <strong>Reference tags:</strong>
                  <ul className="mt-2">
                    <li><code>!item[bom-slug](item-identifier)</code></li>
                    <li><code>!item[bom-slug](item-identifier)(Optional Name)</code></li>
                    <li><code>!bom[bom-slug]</code></li>
                    <li><code>!bom[bom-slug](Optional Name)</code></li>
                    <li><code>!bom[bom-slug, category=Electronics]</code></li>
                    <li><code>!bom[bom-slug, module=Frame]</code></li>
                    <li><code>!bom[bom-slug, version=v2](Optional Name)</code></li>
                    <li><code>!guide[guide-slug]</code></li>
                    <li><code>!guide[guide-slug](Optional Name)</code></li>
                  </ul>
                  <div className="small text-muted">
                    BOM filters open the BOM with the matching category/module view or version filter applied.
                  </div>
                </div>
                <p className="mb-0">
                  Tip: type <code>!item</code>, <code>!bom</code>, or <code>!guide</code> to open autocomplete.
                </p>
              </Modal.Body>
            </Modal>
          </>
          ) : (
            <div style={{cursor: editable ? "context-menu" : "", ...contentStyle}} id={`editable-${index}`} onClick={() => handleContentClick(index, text)}>{(text ? <ReactMarkdown remarkPlugins={markdownRemarkPlugins} components={mergedMarkdownComponents}>{text}</ReactMarkdown> : (editable ? 'Click to edit' : '[empty]'))}</div>
        ))
    );
  }

export default EditableTextArea;
